<template>
  <div class="repair-inventory main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Tồn kho sản phẩm sửa chữa'">
        <template v-slot:preview>
          <!-- Bộ lọc cửa hàng nằm ngoài tab và GIỮ NGUYÊN khi chuyển tab: thủ kho chọn cửa
               hàng một lần rồi xem tồn kho và thẻ kho mà không phải chọn lại. -->
          <b-row class="mb-3">
            <b-col md="4">
              <b-form-group class="mb-0" label="Cửa hàng" label-cols-sm="3">
                <b-form-select
                  v-model="storeId"
                  :options="storeOptions"
                  :disabled="storeLoading || !canPickStore"
                  size="sm"
                  @change="onStoreChange"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group class="mb-0" label="Tìm kiếm" label-cols-sm="3">
                <b-form-input
                  v-model="keyword"
                  size="sm"
                  :placeholder="activeTab === 0 ? 'Tên / mã sản phẩm' : 'IMEI'"
                  @keyup.enter="reload"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4" class="text-right">
              <b-button variant="primary" size="sm" @click="reload">
                <i class="fas fa-search mr-1"></i>Lọc
              </b-button>
              <b-button
                v-if="canViewAll"
                variant="outline-secondary"
                size="sm"
                class="ml-2"
                :disabled="integrity.running"
                title="Đối chiếu số tổng hợp với số máy thực tế trong kho"
                @click="runIntegrityCheck"
              >
                <i class="fas fa-balance-scale mr-1"></i>
                {{ integrity.running ? 'Đang kiểm...' : 'Đối soát' }}
              </b-button>
            </b-col>
          </b-row>

          <b-alert
            v-if="integrity.checked"
            :variant="integrityVariant"
            show
            dismissible
            class="py-2"
            @dismissed="integrity.checked = false"
          >
            <span v-if="integrity.severity === 'ok'">
              Đối soát xong — số tổng hợp khớp với số máy thực tế trong kho, phiếu sửa chữa khớp
              với kho.
            </span>
            <template v-else>
              <div v-if="integrity.slipMismatches.length">
                <strong>{{ integrity.slipMismatches.length }}</strong> máy lệch giữa phiếu sửa chữa
                và kho (quên nhập / quên xuất theo vòng đời phiếu). Vui lòng báo bộ phận kỹ thuật.
              </div>
              <div v-if="integrity.aggregateMismatches.length">
                <strong>
                  {{ integrity.aggregateTruncated ? 'Ít nhất ' : '' }}{{ integrity.aggregateMismatches.length }}
                </strong>
                dòng lệch giữa cột "Tồn đang bảo hành" và số máy chi tiết của kho bảo hành chung.
                <span v-if="!integrity.slipMismatches.length">
                  Không liên quan tới phiếu sửa chữa — thường là dữ liệu kho bảo hành lệch từ trước,
                  cần cân lại kho.
                </span>
              </div>
              <a
                href="javascript:void(0)"
                class="font-weight-bold"
                @click="integrity.showDetail = !integrity.showDetail"
              >
                {{ integrity.showDetail ? 'Ẩn danh sách' : 'Xem danh sách' }}
              </a>
              <div v-if="integrity.showDetail" class="mt-2">
                <b-table
                  v-if="integrity.slipMismatches.length"
                  key="integrity-slip"
                  :items="integrity.slipMismatches"
                  :fields="integritySlipFields"
                  class="bg-white mb-2"
                  bordered
                  small
                  responsive
                >
                  <template #cell(issue)="{ item }">
                    {{ integrityIssueText(item.issue) }}
                  </template>
                </b-table>
                <b-table
                  v-if="integrity.aggregateMismatches.length"
                  key="integrity-aggregate"
                  :items="integrity.aggregateMismatches"
                  :fields="integrityAggregateFields"
                  class="bg-white mb-0"
                  bordered
                  small
                  responsive
                  sticky-header="300px"
                >
                  <template #cell(storeName)="{ item }">
                    {{ item.storeName || ('CH #' + item.storeId) }}
                  </template>
                  <template #cell(productName)="{ item }">
                    <div>{{ item.productName || item.productId }}</div>
                    <div class="small text-muted">{{ item.productCode }}</div>
                  </template>
                </b-table>
              </div>
            </template>
          </b-alert>

          <b-tabs v-model="activeTab" @input="onTabChange">
            <b-tab title="Tồn kho sửa chữa" />
            <b-tab title="Thẻ kho" />
          </b-tabs>

          <!-- ================= TAB 1: Tồn kho tổng hợp theo model ================= -->
          <b-table
            v-if="activeTab === 0"
            key="stock-table"
            class="mt-3 table-vertical-center"
            :items="stockRows"
            :fields="stockFields"
            :busy="loading"
            show-empty
            empty-text="Chưa có máy nào trong kho sửa chữa"
            bordered
            small
            hover
            responsive
          >
            <template #table-busy>
              <div class="text-center text-primary my-3">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ml-2">Đang tải...</strong>
              </div>
            </template>

            <template #cell(product)="{ item }">
              <a
                href="javascript:void(0)"
                class="font-weight-bold"
                @click="openDetail(item)"
              >
                {{ item.productName || '—' }}
              </a>
              <div class="small text-muted">{{ item.productCode }}</div>
            </template>

            <template #cell(store)="{ item }">
              {{ item.storeName || ('CH #' + item.storeId) }}
            </template>

            <template #cell(totalQuantity)="{ item }">
              <span class="font-weight-bold">{{ item.totalQuantity }}</span>
            </template>

            <!-- Sáu cột nhóm dựng động theo STOCK_GROUP_ORDER: thêm/bớt nhóm chỉ sửa constants. -->
            <template
              v-for="group in groupOrder"
              v-slot:[`cell(${group})`]="{ item }"
            >
              <span :key="group" :class="item.groups[group] ? '' : 'text-muted'">
                {{ item.groups[group] || 0 }}
              </span>
            </template>

            <template #cell(maxAgingDays)="{ item }">
              <span
                :class="agingClass(item.agingLevel)"
                :title="agingTitle(item.agingLevel)"
              >
                {{ item.maxAgingDays }} ngày
              </span>
            </template>
          </b-table>

          <!-- ================= TAB 2: Thẻ kho ================= -->
          <!-- `key` riêng cho từng bảng là BẮT BUỘC. Hai <b-table> v-if/v-else không key thì Vue
               tái dùng cùng một instance khi đổi tab, và vì scoped slot biên dịch tĩnh ($stable)
               nên instance giữ nguyên slot của bảng Tồn kho → bảng Thẻ kho mất hết #cell(...):
               hiện giá trị thô ("out", ISO date), cột Cửa hàng/Model/IMEI/Phiếu trống. -->
          <b-table
            v-else
            key="history-table"
            class="mt-3 table-vertical-center"
            :items="historyRows"
            :fields="historyFields"
            :busy="loading"
            show-empty
            empty-text="Không có biến động nào trong khoảng thời gian này"
            bordered
            small
            responsive
          >
            <template #table-busy>
              <div class="text-center text-primary my-3">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ml-2">Đang tải...</strong>
              </div>
            </template>

            <template #cell(createdAt)="{ item }">
              {{ formatDateTime(item.createdAt) }}
            </template>

            <template #cell(code)="{ item }">
              <span class="font-weight-bold">{{ item.code }}</span>
              <div class="small text-muted">{{ item.typeName }}</div>
            </template>

            <template #cell(direction)="{ item }">
              <b-badge :variant="item.direction === 'in' ? 'success' : 'danger'">
                {{ item.direction === 'in' ? 'Nhập +1' : 'Xuất −1' }}
              </b-badge>
            </template>

            <template #cell(historyStore)="{ item }">
              {{ item.storeName || ('CH #' + item.storeId) }}
            </template>

            <template #cell(model)="{ item }">
              <div v-for="d in item.details" :key="'m-' + (d.imei || d.productId)">
                <span class="font-weight-bold">{{ d.productName || '—' }}</span>
                <div v-if="d.productCode" class="small text-muted">{{ d.productCode }}</div>
              </div>
              <span v-if="!item.details.length" class="text-muted">—</span>
            </template>

            <template #cell(imeis)="{ item }">
              <div v-for="d in item.details" :key="d.imei || d.productId">
                {{ d.imei || '—' }}
              </div>
              <span v-if="!item.details.length" class="text-muted">—</span>
            </template>

            <template #cell(slip)="{ item }">
              <!-- BE trả repairSlips (có số phiếu); repairSlipId giữ để tương thích bản BE cũ. -->
              <template v-if="item.slips.length">
                <div v-for="s in item.slips" :key="s.id">
                  <a href="javascript:void(0)" @click="goToSlip(s.id)">
                    {{ s.slipNumber || 'Xem phiếu' }}
                  </a>
                </div>
              </template>
              <span v-else class="text-muted">—</span>
            </template>
          </b-table>

          <b-row class="mt-3 align-items-center">
            <b-col md="6" class="d-flex align-items-center">
              <span class="text-dark">Tổng: {{ totalRow }} dòng</span>
              <b-form-select
                v-model="limit"
                :options="pageSizeOptions"
                size="sm"
                class="ml-3 w-auto"
                @change="onPageSizeChange"
              ></b-form-select>
              <span class="ml-2 text-muted">dòng / trang</span>
              <span v-if="totalPage >= 2" class="ml-3 text-muted">
                Trang {{ page }} / {{ totalPage }}
              </span>
            </b-col>
            <b-col md="6">
              <b-pagination
                v-show="totalPage >= 2"
                v-model="page"
                :total-rows="totalRow"
                :per-page="limit"
                first-number
                last-number
                align="right"
                class="mb-0"
                @change="onPageChange"
              ></b-pagination>
            </b-col>
          </b-row>
        </template>
      </KTCodePreview>
    </div>

    <RepairInventoryDetailModal
      ref="detailModal"
      @open-slip="goToSlip"
      @open-history="goToHistoryOfImei"
    />
  </div>
</template>

<script>
/**
 * SRS t101478 — Tồn kho sản phẩm sửa chữa (FR-02, FR-03, FR-07).
 *
 * Màn hình CHỈ ĐỌC. Tồn kho phát sinh từ vòng đời Phiếu sửa chữa (nhận máy khi rời "Tiếp
 * nhận", trả máy khi đóng phiếu), không có thao tác nhập/xuất kho thủ công ở đây — giữ đúng
 * nguyên tắc phiếu sửa chữa là nguồn phát sinh duy nhất.
 *
 * Sáu nhóm tình trạng KHÔNG phải cột trong DB. BE suy ra từ RepairSlips.status và trả kèm
 * `groups` + `groupNames`; FE chỉ cố định thứ tự hiển thị và màu sắc.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { makeToastFaile } from '@/utils/common';
import { checkPermissions } from '@/utils/saveDataToLocal';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { extractError, formatDateTime } from '@/utils/warranty-service.helpers';
import {
  STOCK_GROUP_META,
  STOCK_GROUP_ORDER,
  AGING_META,
} from '@/utils/warranty-service.constants';
import RepairInventoryDetailModal from '@/view/components/warranty-service/RepairInventoryDetailModal.vue';

/** Trùng tên với VIEW_ALL_PERMISSION ở repair-inventory.service.js (BE) — đổi thì đổi cả hai. */
const VIEW_ALL_PERMISSION = 'REPAIR_STOCK_VIEW_ALL';

const BASE_API = 'v2/warranty-service/repair-inventory';

const STOCK_FIELDS = Object.freeze([
  { key: 'store', label: 'Cửa hàng' },
  { key: 'product', label: 'Model máy' },
  { key: 'totalQuantity', label: 'Đang giữ', tdClass: 'text-center' },
  ...STOCK_GROUP_ORDER.map((group) => ({
    key: group,
    label: STOCK_GROUP_META[group].text,
    tdClass: 'text-center',
  })),
  { key: 'maxAgingDays', label: 'Tồn lâu nhất', tdClass: 'text-center' },
]);

/** Màu theo mức độ BE trả về (repair-inventory-integrity.service.js SEVERITY). */
const INTEGRITY_VARIANT = Object.freeze({ ok: 'success', warning: 'warning', error: 'danger' });

const INTEGRITY_ISSUE_TEXT = Object.freeze({
  missing_in_stock: 'Phiếu còn mở nhưng máy không có trong kho',
  orphan_in_stock: 'Phiếu đã đóng nhưng máy vẫn còn trong kho',
});

const INTEGRITY_AGGREGATE_FIELDS = Object.freeze([
  { key: 'storeName', label: 'Cửa hàng' },
  { key: 'productName', label: 'Model máy' },
  { key: 'aggregateQuantity', label: 'Tồn đang BH (tổng hợp)', tdClass: 'text-center' },
  { key: 'detailCount', label: 'Số máy chi tiết', tdClass: 'text-center' },
  { key: 'diff', label: 'Lệch', tdClass: 'text-center' },
]);

const INTEGRITY_SLIP_FIELDS = Object.freeze([
  { key: 'slipNumber', label: 'Phiếu sửa chữa' },
  { key: 'imei', label: 'IMEI' },
  { key: 'slipStatus', label: 'Trạng thái phiếu' },
  { key: 'issue', label: 'Vấn đề' },
]);

/** Trần 200 trùng MAX_PAGE_SIZE ở repair-inventory.service.js (BE) — vượt sẽ bị BE kẹp lại. */
const PAGE_SIZE_OPTIONS = Object.freeze([10, 20, 50, 100, 200]);

const HISTORY_FIELDS = Object.freeze([
  { key: 'createdAt', label: 'Thời điểm' },
  { key: 'code', label: 'Chứng từ' },
  { key: 'historyStore', label: 'Cửa hàng' },
  { key: 'model', label: 'Model máy' },
  { key: 'imeis', label: 'IMEI' },
  { key: 'direction', label: 'Biến động', tdClass: 'text-center' },
  { key: 'slip', label: 'Phiếu sửa chữa', tdClass: 'text-center' },
]);

export default {
  name: 'RepairInventory',
  components: { KTCodePreview, RepairInventoryDetailModal },
  data() {
    return {
      activeTab: 0,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      storeId: null,
      storeOptions: [{ value: null, text: 'Tất cả cửa hàng' }],
      storeLoading: false,
      canPickStore: false,
      canViewAll: false,
      keyword: '',
      stockRows: [],
      historyRows: [],
      loading: false,
      page: 1,
      limit: 20,
      totalRow: 0,
      totalPage: 0,
      integrity: {
        running: false,
        checked: false,
        severity: 'ok',
        aggregateTruncated: false,
        aggregateMismatches: [],
        slipMismatches: [],
        showDetail: false,
      },
    };
  },
  computed: {
    stockFields: () => STOCK_FIELDS,
    historyFields: () => HISTORY_FIELDS,
    integrityAggregateFields: () => INTEGRITY_AGGREGATE_FIELDS,
    integritySlipFields: () => INTEGRITY_SLIP_FIELDS,
    integrityVariant() {
      return INTEGRITY_VARIANT[this.integrity.severity] || 'secondary';
    },
    groupOrder: () => STOCK_GROUP_ORDER,
  },
  created() {
    // Không để trong data(): chỉ là bộ đếm nội bộ, không cần reactive.
    this.requestSeq = 0;
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Dịch vụ bảo hành' },
      { title: 'Tồn kho sản phẩm sửa chữa' },
    ]);
    await this.fetchStores();
    this.fetchData();
  },
  methods: {
    formatDateTime,
    agingClass(level) {
      return (AGING_META[level] && AGING_META[level].variant) || '';
    },
    agingTitle(level) {
      return (AGING_META[level] && AGING_META[level].title) || '';
    },
    async fetchStores() {
      this.storeLoading = true;
      try {
        const { data } = await ApiService.query('stores/get-by-user');
        const payload = (data && data.data) || {};
        const stores = payload.stores || [];
        // Lấy quyền từ listFeature, KHÔNG dùng payload.viewAll của stores/get-by-user:
        // BE set cờ đó theo CHỨC DANH (StoreController.getUserStore — QL/TL/KTBH/TVBH thì 1
        // cửa hàng, chức danh khác thì xem tất cả), không theo REPAIR_STOCK_VIEW_ALL. Hai thứ
        // lệch nhau thì màn hình hiện nút "Đối soát" cho người không có quyền (bấm vào ăn 403),
        // hoặc giấu mất tính năng xem-tất-cả của người có quyền.
        this.canViewAll = checkPermissions([VIEW_ALL_PERMISSION]);
        // Quyền xem cửa hàng mình = đúng MỘT cửa hàng (repair-inventory.service.js
        // resolveStoreScope đọc storeId trong JWT), nên chỉ người xem-tất-cả mới được đổi.
        // Cho chọn theo stores.length sẽ mời user bấm vào cửa hàng mà BE chắc chắn chặn.
        this.canPickStore = this.canViewAll;
        this.storeOptions = [
          { value: null, text: 'Tất cả cửa hàng' },
          ...stores.map((s) => ({ value: s.id, text: s.name })),
        ];
        if (!this.canViewAll && payload.storeId) {
          this.storeId = payload.storeId;
        }
      } catch (err) {
        makeToastFaile('Không tải được danh sách cửa hàng');
      } finally {
        this.storeLoading = false;
      }
    },
    onStoreChange() {
      this.page = 1;
      this.$nextTick(() => this.fetchData());
    },
    onTabChange() {
      // Giữ nguyên bộ lọc cửa hàng, chỉ reset phân trang và kết quả cũ.
      this.page = 1;
      this.stockRows = [];
      this.historyRows = [];
      this.totalRow = 0;
      this.fetchData();
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.$nextTick(() => this.fetchData());
    },
    onPageSizeChange() {
      this.page = 1;
      this.$nextTick(() => this.fetchData());
    },
    reload() {
      this.page = 1;
      this.fetchData();
    },
    async fetchData() {
      // Bấm chuyển trang / đổi tab liên tục: response cũ về SAU response mới sẽ đè dữ liệu
      // sai trang. Đánh số lượt gọi, chỉ lượt mới nhất được ghi kết quả.
      const seq = ++this.requestSeq;
      const isStale = () => seq !== this.requestSeq;
      this.loading = true;
      try {
        if (this.activeTab === 0) {
          await this.fetchStocks(isStale);
        } else {
          await this.fetchHistories(isStale);
        }
      } catch (err) {
        if (isStale()) return;
        makeToastFaile(extractError(err));
        this.stockRows = [];
        this.historyRows = [];
        this.totalRow = 0;
      } finally {
        if (!isStale()) this.loading = false;
      }
    },
    async fetchStocks(isStale = () => false) {
      const { data } = await ApiService.query(`${BASE_API}/stocks`, {
        params: {
          storeId: this.storeId || undefined,
          keyword: this.keyword || undefined,
          page: this.page,
          limit: this.limit,
        },
      });
      if (isStale()) return;
      const payload = (data && data.data) || {};
      this.stockRows = payload.items || [];
      this.totalRow = payload.total || 0;
      this.totalPage = Math.ceil(this.totalRow / this.limit);
    },
    async fetchHistories(isStale = () => false) {
      const { data } = await ApiService.query(`${BASE_API}/histories`, {
        params: {
          storeId: this.storeId || undefined,
          // Ở tab thẻ kho, ô tìm kiếm dùng để lọc theo IMEI.
          imei: this.keyword || undefined,
          page: this.page,
          limit: this.limit,
        },
      });
      if (isStale()) return;
      const payload = (data && data.data) || {};
      // Chuẩn hoá một lần lúc nạp, không tính lại mỗi lần render.
      this.historyRows = (payload.items || []).map((item) => ({
        ...item,
        details: item.details || [],
        slips: (item.repairSlips && item.repairSlips.length)
          ? item.repairSlips
          : (item.repairSlipId ? [{ id: item.repairSlipId, slipNumber: null }] : []),
      }));
      this.totalRow = payload.total || 0;
      this.totalPage = Math.ceil(this.totalRow / this.limit);
    },
    openDetail(row) {
      this.$refs.detailModal.open(row);
    },
    goToSlip(id) {
      const route = this.$router.resolve({ name: 'edit-repair-slip', params: { id } });
      window.open(route.href, '_blank');
    },
    /** Từ chi tiết một máy nhảy sang tab thẻ kho đã lọc sẵn IMEI đó. */
    goToHistoryOfImei(imei) {
      this.$bvModal.hide('modal-repair-inventory-detail');
      this.keyword = imei || '';
      this.page = 1;
      // Đổi activeTab làm b-tabs bắn `input` → onTabChange() đã gọi fetchData(). Gọi thêm
      // một lần ở đây nữa là bắn hai request /histories giống hệt nhau. Chỉ tự gọi khi đang
      // sẵn ở tab thẻ kho, vì lúc đó giá trị không đổi nên không có sự kiện nào bắn ra.
      if (this.activeTab === 1) {
        this.fetchData();
      } else {
        this.activeTab = 1;
      }
    },
    integrityIssueText(issue) {
      return INTEGRITY_ISSUE_TEXT[issue] || issue;
    },
    async runIntegrityCheck() {
      this.integrity.running = true;
      try {
        const { data } = await ApiService.query(`${BASE_API}/integrity-check`, {
          params: { storeId: this.storeId || undefined },
        });
        const payload = (data && data.data) || {};
        const aggregateMismatches = payload.aggregateMismatches || [];
        const slipMismatches = payload.slipMismatches || [];
        // BE cũ chưa trả severity → suy lại theo cùng quy tắc.
        let { severity } = payload;
        if (!severity) {
          if (slipMismatches.length) severity = 'error';
          else severity = aggregateMismatches.length ? 'warning' : 'ok';
        }
        Object.assign(this.integrity, {
          severity,
          aggregateMismatches,
          slipMismatches,
          aggregateTruncated: !!payload.aggregateTruncated,
          showDetail: false,
          checked: true,
        });
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.integrity.running = false;
      }
    },
  },
};
</script>
